import { CalendarIcon } from "@xola/icons";
import { FadeIn, numberFormat, Skeleton } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, getExperienceMin, isExperienceProduct, now } from "common";
import React, { useEffect, useState } from "react";
import shallow from "zustand/shallow";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { Breakdown } from "../Common/Breakdown";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { PurchasePackageSidebarPreview } from "./PurchasePackageSidebarPreview";
import { useSeller } from "@/hooks/useAccount";
import { getDemographicById } from "@/models/Experience";
import { isScheduled } from "@/models/Product";
import { useLocalizationStore } from "@/stores/localizationStore";

const durationErrorMessage = "Duration between all items should be greater than";

export const CreatePurchaseSidebarSkeleton = () => {
    const [product, arrivalDate, templateId, lineItemNameFromItem, isPackageBooking] = useCreatePurchaseStore(
        (state) => {
            const item = state.getActiveItem();
            const isPackageBooking = !!item.parentProduct;

            return [
                state.getActiveProduct(),
                now(item.arrivalDate).format(DateFormat.DAY_WITH_FULL_MONTH_NAME_DATE),
                item.demographicLineItems?.[0]?.template?.id,
                item.lineItems?.[0] && "name" in item.lineItems[0] ? item.lineItems[0].name : "",
                isPackageBooking,
            ];
        },
        shallow,
    );
    const hasDurationError = useCreatePurchaseStore((state) =>
        state.errors?.some((error) => String(error.message).includes(durationErrorMessage)),
    );

    const isExperience = isExperienceProduct(product);

    const [showLoadingSkeleton, setShowLoadingSkeleton] = useState(true);

    const hasAvailableTimeslot = useCreatePurchaseStore((state) => state.hasAvailableTimeslot);
    const timeslot = useCreatePurchaseStore((state) => state.getActiveItem().timeslot);
    const hasNoAvailability = hasAvailableTimeslot === false && !timeslot;

    let quantity = 1;
    let price;
    let lineItemName = lineItemNameFromItem;

    if (isExperience) {
        const demographic = isExperience ? getDemographicById(product, templateId) : null;

        quantity = getExperienceMin(product);
        price = demographic?.prices?.price?.min;
        lineItemName = demographic?.name ?? "";
    }

    useEffect(() => {
        if (hasNoAvailability) {
            setShowLoadingSkeleton(false);
        }
    }, [hasNoAvailability, setShowLoadingSkeleton]);

    const styling = showLoadingSkeleton ? "border-primary bg-primary-lighter" : "border-secondary bg-secondary-lighter";
    return (
        <div className="sticky top-0 space-y-6 px-10 py-7">
            <h3>Purchase Summary</h3>
            {hasDurationError && isPackageBooking ? (
                <PurchasePackageSidebarPreview />
            ) : (
                <div className="flex flex-col space-y-4">
                    <div className={clsx("space-y-3 rounded border !py-5 !px-6", styling)}>
                        <div className="flex justify-between space-x-1">
                            <div className="flex items-center">
                                <ProductImage product={product} size="small" className="!mr-1" />
                                <div className="w-full">
                                    <div className="text-md font-bold">{product.name}</div>
                                    <div className="flex w-full space-x-2 text-gray-darker">
                                        {arrivalDate && isScheduled(product) && (
                                            <span>
                                                <CalendarIcon />
                                                &nbsp;
                                                <NoTranslate data-testid="purchase-item-summary-date">
                                                    {arrivalDate}
                                                </NoTranslate>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {showLoadingSkeleton && (
                            <BreakdownLineItem quantity={quantity} price={price} lineItemName={lineItemName} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const BreakdownLineItem = ({
    quantity = 1,
    price,
    lineItemName,
}: {
    quantity: number;
    price?: number;
    lineItemName?: string;
}) => {
    const { currency } = useSeller();
    const locale = useLocalizationStore.getState().getLocale();

    return (
        <FadeIn>
            {price && lineItemName ? (
                <Breakdown currency={currency}>
                    <Breakdown.Item value={price}>
                        <span>{lineItemName}</span>
                        <span>&nbsp;({[numberFormat(price, currency, locale), "×", quantity].join(" ")})</span>
                    </Breakdown.Item>

                    <tr className={clsx("ui-breakdown-separator")}>
                        <td colSpan={3} className="border-b border-black/10 pb-1" />
                    </tr>
                </Breakdown>
            ) : null}
            <Skeleton height={20} classNames={{ container: "mr-3 !border-none !bg-primary-lighter" }} />
        </FadeIn>
    );
};
